import React, { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select } from '../ui/newSelect';

const titleCase = (value) =>
  String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const money = (value, currency = 'GBP') =>
  value == null || Number.isNaN(Number(value))
    ? '—'
    : new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(value));

const roundHalfUp = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/**
 * Preview of the Partner Account Fee schedule: catalogue standard price less the Partner discount,
 * mirroring `derivePartnerAccountFeeSchedule` on the backend.
 * @param {object} schedule catalogue `{ currency, plans, fteBands, matrix, addOns }`
 * @param {number|string} discountPercent
 * @returns {object|null} `{ currency, discountPercent, plans: { plan: { band: { standard, fee } } }, addOns }`
 */
export const derivePartnerFeePreview = (schedule, discountPercent) => {
  if (!schedule?.matrix) return null;
  const d = Number(discountPercent) || 0;
  const fee = (standard) => (standard == null ? null : roundHalfUp(standard * (1 - d / 100)));
  const plans = {};
  (schedule.plans || Object.keys(schedule.matrix)).forEach((plan) => {
    plans[plan] = {};
    (schedule.fteBands || Object.keys(schedule.matrix[plan] || {})).forEach((band) => {
      const standard = schedule.matrix?.[plan]?.[band]?.standard;
      plans[plan][band] = { standard, fee: fee(standard) };
    });
  });
  const addOns = (schedule.addOns || [])
    .filter((a) => a.standardAnnualPrice != null)
    .map((a) => ({ id: a.id, name: a.name, standard: a.standardAnnualPrice, fee: fee(a.standardAnnualPrice), includedPlanIds: a.includedPlanIds || [] }));
  return { currency: schedule.currency, discountPercent: d, plans, addOns };
};

const bandLowerBound = (band) => parseInt(String(band), 10) || 0;
const cheapestStandard = (bands) => Math.min(...Object.values(bands || {}).map((c) => Number(c?.standard) || Infinity));

/**
 * Plan × FTE rows in catalogue order (plans by cheapest standard price, bands by lower bound);
 * stored schedules are maps, so key order is not preserved.
 * @param {object} plans `{ plan: { band: { standard, fee } } }`
 * @returns {Array<[string, string, object]>}
 */
export const orderedFeeRows = (plans = {}) =>
  Object.entries(plans)
    .sort(([, a], [, b]) => cheapestStandard(a) - cheapestStandard(b))
    .flatMap(([plan, bands]) =>
      Object.entries(bands || {})
        .sort(([a], [b]) => bandLowerBound(a) - bandLowerBound(b))
        .map(([band, cell]) => [plan, band, cell])
    );

/** Full Partner Account Fee table (R-6098): every Plan × FTE band and Add-on, standard and Partner fee. */
export const PartnerFeeTable = ({ feeSchedule }) => {
  const rows = useMemo(() => orderedFeeRows(feeSchedule?.plans), [feeSchedule]);
  if (!feeSchedule?.plans) return null;
  const currency = feeSchedule.currency || 'GBP';
  const custom = feeSchedule.scheduleMode === 'custom_packages';
  return (
    <div className='space-y-4'>
      <div className='overflow-x-auto rounded-md border border-border'>
        <Table>
          <TableHeader>
            <TableRow className='border-border'>
              <TableHead>{custom ? 'Package' : 'Plan'}</TableHead>
              <TableHead>FTE</TableHead>
              {!custom && <TableHead className='text-right'>Standard annual price</TableHead>}
              <TableHead className='text-right'>Partner Account Fee / year</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(([plan, band, cell]) => (
              <TableRow key={`${plan}-${band}`} className='border-border'>
                <TableCell className='font-medium'>{titleCase(plan)}</TableCell>
                <TableCell>{band}</TableCell>
                {!custom && (
                  <TableCell className='text-right text-muted-foreground'>{money(cell?.standard, currency)}</TableCell>
                )}
                <TableCell className='text-right'>{money(cell?.fee, currency)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {(feeSchedule.addOns || []).length > 0 && (
        <div className='overflow-x-auto rounded-md border border-border'>
          <Table>
            <TableHeader>
              <TableRow className='border-border'>
                <TableHead>Add-on</TableHead>
                <TableHead className='text-right'>Standard annual price</TableHead>
                <TableHead className='text-right'>Partner Account Fee / year</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {feeSchedule.addOns.map((a) => (
                <TableRow key={a.id} className='border-border'>
                  <TableCell className='font-medium'>
                    {a.name}
                    {a.includedPlanIds?.length ? (
                      <span className='ml-1 text-xs text-muted-foreground'>(included in {a.includedPlanIds.map(titleCase).join(', ')})</span>
                    ) : null}
                  </TableCell>
                  <TableCell className='text-right text-muted-foreground'>{money(a.standard, currency)}</TableCell>
                  <TableCell className='text-right'>{money(a.fee, currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

/** Plain-language Partner Reseller terms (R-6097, R-6119). */
export const partnerTermsLines = (terms, currency = 'GBP') => {
  if (!terms) return [];
  const fee = Number(terms.licenceFee) || 0;
  const cadence = terms.accountFeeCadence === 'quarterly' ? 'quarterly' : 'monthly';
  const accountFeesLine =
    terms.scheduleMode === 'custom_packages'
      ? `Partner Account Fees from the agreed White Label packages, invoiced ${cadence} in arrears and payable within 30 days`
      : `Partner Account Fees at ${Number(terms.discountPercent || 0).toFixed(2)} percent below the catalogue, invoiced ${cadence} in arrears and payable within 30 days`;
  return [
    fee > 0
      ? `Partner Licence Fee ${money(fee, currency)} per year, ${terms.licenceFeeFrequency === 'monthly' ? 'invoiced monthly in twelve parts' : 'invoiced annually in advance'}`
      : 'No Partner Licence Fee',
    accountFeesLine,
    terms.minimumCommitment != null && terms.minimumCommitment !== ''
      ? `Partner Minimum Commitment ${money(terms.minimumCommitment, currency)} of Partner Account Fees per commitment year`
      : 'No minimum commitment',
    `${terms.termMonths || 12}-month commitment${terms.startDate ? ` from ${terms.startDate}` : ''}, renewing for 12 months unless either party gives 180 days' notice`,
  ];
};

/** Build a PartnerFeeTable-shaped schedule from custom packages (admin preview / review). */
export const feeScheduleFromCustomPackagesPreview = (packages, currency = 'GBP') => {
  const plans = {};
  for (const pkg of packages || []) {
    const name = String(pkg.name || '').trim();
    if (!name) continue;
    plans[name] = {};
    const bands = Array.isArray(pkg.fteBands) ? pkg.fteBands : [];
    const fees = Array.isArray(pkg.annualFees) ? pkg.annualFees : [];
    bands.forEach((band, i) => {
      const fee = Number(fees[i]) || 0;
      plans[name][band] = { standard: fee, fee };
    });
  }
  return { currency, discountPercent: 0, scheduleMode: 'custom_packages', plans, addOns: [] };
};

const firstOfNextMonth = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString().slice(0, 10);
};

/** Order-form inputs for a Partner Reseller Agreement (admin). */
export const PartnerTermsForm = ({ value, onChange, schedule, symbol }) => {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const scheduleMode = v.scheduleMode === 'custom_packages' ? 'custom_packages' : 'catalogue_discount';
  const packages = Array.isArray(v.customPackages) ? v.customPackages : [];
  const preview = useMemo(() => derivePartnerFeePreview(schedule, v.discountPercent), [schedule, v.discountPercent]);
  const customPreview = useMemo(
    () => feeScheduleFromCustomPackagesPreview(packages, schedule?.currency || 'GBP'),
    [packages, schedule?.currency]
  );
  const seedFromCatalogue = (discount) => {
    const matrix = schedule?.matrix || {};
    const seeded = Object.keys(matrix).map((planId) => {
      const bands = Object.keys(matrix[planId] || {});
      const annualFees = bands.map((b) => {
        const standard = Number(matrix[planId][b]?.standard) || 0;
        return Math.round(standard * (1 - (Number(discount) || 0) / 100) * 100) / 100;
      });
      return { name: planId, fteBands: bands, annualFees, capabilities: [], unlocksSwc: false };
    });
    onChange({ ...v, scheduleMode: 'custom_packages', customPackages: seeded, discountPercent: discount });
  };
  const setPackage = (i, patch) => {
    const next = packages.map((p, j) => (j === i ? { ...p, ...patch } : p));
    set('customPackages', next);
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>White Label Reseller terms</CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div>
            <Label>Partner Licence Fee per year ({symbol}) *</Label>
            <Input type='number' min='0' step='0.01' value={v.licenceFee ?? ''} onChange={(e) => set('licenceFee', e.target.value)} placeholder='0 for none' />
          </div>
          <div>
            <Label>Licence fee billing</Label>
            <Select
              value={v.licenceFeeFrequency || 'annual'}
              onChange={(val) => set('licenceFeeFrequency', val)}
              options={[
                { value: 'annual', label: 'Annually in advance' },
                { value: 'monthly', label: 'Monthly (twelve parts)' },
              ]}
            />
          </div>
          <div>
            <Label>Account fee cadence</Label>
            <Select
              value={v.accountFeeCadence || 'monthly'}
              onChange={(val) => set('accountFeeCadence', val)}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
              ]}
            />
          </div>
          <div>
            <Label>Schedule mode</Label>
            <Select
              value={scheduleMode}
              onChange={(val) => set('scheduleMode', val)}
              options={[
                { value: 'catalogue_discount', label: 'Catalogue − one %' },
                { value: 'custom_packages', label: 'Custom White Label packages' },
              ]}
            />
          </div>
          {scheduleMode === 'catalogue_discount' && (
            <div>
              <Label>Partner discount %</Label>
              <Input type='number' min='0' max='100' step='0.01' value={v.discountPercent ?? ''} onChange={(e) => set('discountPercent', e.target.value)} placeholder='0' />
            </div>
          )}
          <div>
            <Label>Minimum commitment per year ({symbol})</Label>
            <Input type='number' min='0' step='0.01' value={v.minimumCommitment ?? ''} onChange={(e) => set('minimumCommitment', e.target.value)} placeholder='Leave empty for none' />
          </div>
          <div>
            <Label>Commitment</Label>
            <Select
              value={String(v.termMonths || 12)}
              onChange={(val) => set('termMonths', Number(val))}
              options={[
                { value: '12', label: '12 months' },
                { value: '36', label: '36 months' },
              ]}
            />
          </div>
          <div>
            <Label>Start date (first of a month)</Label>
            <Input type='date' value={v.startDate || ''} onChange={(e) => set('startDate', e.target.value)} placeholder={firstOfNextMonth()} />
          </div>
        </div>
        {scheduleMode === 'custom_packages' && (
          <div className='space-y-3 border border-border rounded-md p-3'>
            <div className='flex flex-wrap gap-2 items-center justify-between'>
              <h3 className='text-sm font-semibold'>White Label packages (Agreement / Sales)</h3>
              <div className='flex gap-2'>
                <Button type='button' size='sm' variant='outline' onClick={() => seedFromCatalogue(v.discountPercent || 20)}>
                  Mirror catalogue + % off
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => set('customPackages', [...packages, { name: '', fteBands: ['1-10'], annualFees: [0], capabilities: [], unlocksSwc: false }])}>
                  Add package
                </Button>
              </div>
            </div>
            {packages.map((pkg, i) => (
              <div key={i} className='grid grid-cols-1 md:grid-cols-4 gap-2 border-t border-border pt-2'>
                <div>
                  <Label className='text-xs'>Package name</Label>
                  <Input value={pkg.name || ''} onChange={(e) => setPackage(i, { name: e.target.value })} />
                </div>
                <div>
                  <Label className='text-xs'>FTE bands (comma)</Label>
                  <Input
                    value={(pkg.fteBands || []).join(', ')}
                    onChange={(e) => setPackage(i, { fteBands: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                  />
                </div>
                <div>
                  <Label className='text-xs'>CYF annual fees (comma)</Label>
                  <Input
                    value={(pkg.annualFees || []).join(', ')}
                    onChange={(e) =>
                      setPackage(i, {
                        annualFees: e.target.value.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n)),
                      })
                    }
                  />
                </div>
                <div className='flex items-end gap-2'>
                  <label className='text-xs flex items-center gap-1'>
                    <input
                      type='checkbox'
                      checked={!!pkg.unlocksSwc}
                      onChange={(e) => setPackage(i, { unlocksSwc: e.target.checked })}
                    />
                    SWC (needs FTE)
                  </label>
                  <Button type='button' size='sm' variant='ghost' onClick={() => set('customPackages', packages.filter((_, j) => j !== i))}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            <p className='text-xs text-muted-foreground'>
              Sell-to-client prices are optional and never invoiced by CYF. Consultants cannot use custom packages.
            </p>
            {Object.keys(customPreview.plans || {}).length > 0 && (
              <div>
                <h3 className='mb-2 text-sm font-semibold text-foreground'>White Label packages (preview)</h3>
                <PartnerFeeTable feeSchedule={customPreview} />
              </div>
            )}
          </div>
        )}
        <ul className='list-disc pl-5 text-sm text-foreground space-y-1'>
          {partnerTermsLines({ ...v, scheduleMode }, schedule?.currency).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        {scheduleMode === 'catalogue_discount' && (
          <div>
            <h3 className='mb-2 text-sm font-semibold text-foreground'>Partner Account Fees (preview)</h3>
            <PartnerFeeTable feeSchedule={preview} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PAYOUT_CURRENCIES = [
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'EUR', label: 'EUR (€)' },
];

/** Plain-language Ambassador terms preview, mirroring `describeAmbassadorTerms`. */
export const ambassadorTermsLines = (terms) => {
  const periods = terms?.revenueSharePeriods || [];
  if (!periods.length) return ['No revenue share'];
  const lines = periods.map((p) => {
    const duration = p.years === 'indefinite' ? 'an indefinite period' : `${p.years} year${Number(p.years) === 1 ? '' : 's'}`;
    return `${Number(p.percent)} percent of Attributed Revenue for ${duration}`;
  });
  if (terms.minimumPortfolio) lines.push(`Shares are payable once ${terms.minimumPortfolio} Referred Customers are paying`);
  lines.push(`Paid in ${terms.payoutCurrency || 'GBP'} by monthly self-billing statement once the balance reaches 100`);
  return lines;
};

/** Order-form inputs for an Ambassador Agreement (admin). */
export const AmbassadorTermsForm = ({ value, onChange }) => {
  const v = value || { revenueSharePeriods: [], payoutCurrency: 'GBP' };
  const periods = v.revenueSharePeriods || [];
  const setPeriods = (next) => onChange({ ...v, revenueSharePeriods: next });
  const setPeriod = (i, k, val) => setPeriods(periods.map((p, j) => (j === i ? { ...p, [k]: val } : p)));
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ambassador terms</CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-sm text-muted-foreground'>
          Leave revenue share empty for a monitor-only Ambassador. Either party may end the agreement with 30 days&apos; notice.
        </p>
        {periods.map((p, i) => (
          <div key={i} className='grid grid-cols-1 md:grid-cols-6 gap-2 items-end border border-border rounded-md p-2'>
            <div className='md:col-span-2'>
              <Label className='text-xs'>Share %</Label>
              <Input type='number' min='0' max='100' step='0.01' value={p.percent ?? ''} onChange={(e) => setPeriod(i, 'percent', e.target.value)} />
            </div>
            <div className='md:col-span-3'>
              <Label className='text-xs'>Duration</Label>
              <Select
                value={String(p.years ?? '1')}
                onChange={(val) => setPeriod(i, 'years', val === 'indefinite' ? 'indefinite' : Number(val))}
                options={[
                  ...Array.from({ length: 10 }, (_, k) => ({ value: String(k + 1), label: `${k + 1} year${k ? 's' : ''}` })),
                  ...(i === periods.length - 1 ? [{ value: 'indefinite', label: 'Indefinite' }] : []),
                ]}
              />
            </div>
            <div className='flex justify-end'>
              <Button type='button' variant='ghost' size='sm' onClick={() => setPeriods(periods.filter((_, j) => j !== i))}>
                <Trash2 size={16} strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        ))}
        <Button type='button' variant='outline' onClick={() => setPeriods([...periods, { percent: '', years: 1 }])}>
          <Plus size={16} strokeWidth={1.5} className='mr-1' />
          Add revenue share period
        </Button>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <Label>Minimum portfolio (paying customers)</Label>
            <Input
              type='number'
              min='1'
              step='1'
              value={v.minimumPortfolio ?? ''}
              onChange={(e) => onChange({ ...v, minimumPortfolio: e.target.value })}
              placeholder='Leave empty for none'
              disabled={!periods.length}
            />
          </div>
          <div>
            <Label>Payout currency *</Label>
            <Select value={v.payoutCurrency || 'GBP'} onChange={(val) => onChange({ ...v, payoutCurrency: val })} options={PAYOUT_CURRENCIES} />
          </div>
        </div>
        <ul className='list-disc pl-5 text-sm text-foreground space-y-1'>
          {ambassadorTermsLines(v).map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

/** Customer-facing review of Partner Reseller terms (public agreement page). */
export const PartnerTermsReview = ({ terms, feeSchedule, className = '' }) => {
  if (!terms) return null;
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className='text-base'>Partner Reseller terms</CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        <ul className='list-disc pl-5 text-sm text-foreground space-y-1'>
          {partnerTermsLines(terms, feeSchedule?.currency).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p className='text-xs text-muted-foreground'>
          Each Active Partner client account that is not a Demo Account is charged one twelfth of its annual Partner Account Fee
          for each month, at the highest package (or Plan), FTE band and Add-ons it held in that month.
        </p>
        <h3 className='text-sm font-semibold text-foreground'>
          {terms.scheduleMode === 'custom_packages' ? 'White Label packages' : 'Partner Account Fees'}
        </h3>
        <PartnerFeeTable feeSchedule={feeSchedule} />
      </CardContent>
    </Card>
  );
};

/** Customer-facing review of Ambassador terms (public agreement page). */
export const AmbassadorTermsReview = ({ summary, className = '' }) => {
  if (!summary) return null;
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className='text-base'>Ambassador terms</CardTitle>
      </CardHeader>
      <CardContent className='space-y-2'>
        <ul className='list-disc pl-5 text-sm text-foreground space-y-1'>
          {summary.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
        <p className='text-xs text-muted-foreground'>
          Either party may end this agreement with 30 days&apos; notice. This agreement does not make you a Consultant or give
          access to client accounts.
        </p>
      </CardContent>
    </Card>
  );
};
